import { Link, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { CollaborationPaths } from './CollaborationPaths'
import type { ReactNode } from 'react'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { TelemetryDiagram } from '@/components/lab/TelemetryDiagram'

export const CAPABILITY_PATHS = {
  iot: '/iot',
  cloud: '/cloud-engineering',
  ai: '/ai',
  data: '/data-analytics',
} as const

type Passage = { title: string; body: string }
type Capability = {
  title: string
  intro: string
  scope: Array<Passage>
  decisions: Array<Passage>
  deliverables: Array<string>
  proofTitle: string
  proof: string
  proofLink: string
}
function usePage() {
  const { t } = useTranslation('lab')
  const locale = getLocaleFromPath(useLocation().pathname)
  return { t, locale, href: (path: string) => withLocalePath(path, locale) }
}
export function PageIntro({
  title,
  intro,
  children,
}: {
  title: string
  intro: string
  children?: ReactNode
}) {
  return (
    <header className="journal-width engineering-intro">
      <h1>{title}</h1>
      <div>
        <p>{intro}</p>
        {children}
      </div>
    </header>
  )
}
export function PassageList({ items }: { items: Array<Passage> }) {
  return (
    <div className="engineering-passages">
      {items.map((item) => (
        <article key={item.title}>
          <h3>{item.title}</h3>
          <p>{item.body}</p>
        </article>
      ))}
    </div>
  )
}
export function EngineeringCta() {
  const { t, href } = usePage()
  return (
    <section className="journal-width engineering-cta">
      <h2>{t('journal.consultingTitle')}</h2>
      <div>
        <p>{t('journal.consultingBody')}</p>
        <Link to={href('/work-with-us')} className="journal-link">
          {t('journal.consultingLink')}
          <ArrowUpRight size={18} aria-hidden="true" />
        </Link>
      </div>
    </section>
  )
}
export function CapabilityPage({
  kind,
}: {
  kind: 'iot' | 'cloudEngineering' | 'ai' | 'dataAnalytics'
}) {
  const { t, href } = usePage()
  const content = t(`site.${kind}`, {
    returnObjects: true,
  }) as unknown as Capability
  const proofPath =
    kind === 'cloudEngineering'
      ? '/cloud'
      : kind === 'ai'
        ? '/research'
        : '/work-with-us'
  return (
    <div className="engineering-page">
      <div className="journal-width engineering-breadcrumb">
        <Link to={href('/what-we-do')}>{t('site.labels.back')}</Link>
        <span aria-hidden="true">/</span>
        <span>
          {t(
            `journal.capabilities.${kind === 'cloudEngineering' ? 'cloud' : kind === 'dataAnalytics' ? 'data' : kind}.name`,
          )}
        </span>
      </div>
      <PageIntro title={content.title} intro={content.intro}>
        <Link className="journal-link" to={href('/work-with-us')}>
          {t('site.labels.discuss')}
          <ArrowUpRight size={18} aria-hidden="true" />
        </Link>
      </PageIntro>
      {kind === 'iot' && (
        <section className="journal-width engineering-diagram">
          <TelemetryDiagram />
        </section>
      )}
      <section className="journal-width engineering-section">
        <h2>{t('site.labels.scope')}</h2>
        <PassageList items={content.scope} />
      </section>
      <section className="engineering-decisions">
        <div className="journal-width engineering-section">
          <h2>{t('site.labels.decisions')}</h2>
          <PassageList items={content.decisions} />
        </div>
      </section>
      <section className="journal-width engineering-section engineering-deliverables">
        <h2>{t('site.labels.deliverables')}</h2>
        <ul>
          {content.deliverables.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>
      <section className="journal-width engineering-proof">
        <h2>{content.proofTitle}</h2>
        <div>
          <p>{content.proof}</p>
          {kind !== 'iot' && (
            <Link to={href(proofPath)} className="journal-link">
              {content.proofLink}
              <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
          )}
        </div>
      </section>
      <section className="journal-width engineering-related">
        <h2>{t('site.labels.related')}</h2>
        <div>
          {Object.entries(CAPABILITY_PATHS)
            .filter(
              ([key]) =>
                key !==
                (kind === 'cloudEngineering'
                  ? 'cloud'
                  : kind === 'dataAnalytics'
                    ? 'data'
                    : kind),
            )
            .map(([key, path]) => (
              <Link key={key} to={href(path)}>
                {t(`journal.capabilities.${key}.name`)}
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            ))}
        </div>
      </section>
      <EngineeringCta />
    </div>
  )
}
export function WhatWeDoPage() {
  const { t, href } = usePage()
  const principles = t('site.overview.principles', {
    returnObjects: true,
  }) as unknown as Array<Passage>
  return (
    <div className="engineering-page">
      <PageIntro
        title={t('site.overview.title')}
        intro={t('site.overview.intro')}
      />
      <section className="journal-width engineering-directory">
        {Object.entries(CAPABILITY_PATHS).map(([key, path]) => (
          <Link key={key} to={href(path)}>
            <div>
              <h2>{t(`journal.capabilities.${key}.name`)}</h2>
              <span>{t(`journal.capabilities.${key}.short`)}</span>
            </div>
            <p>{t(`journal.capabilities.${key}.body`)}</p>
            <ArrowUpRight size={28} aria-hidden="true" />
          </Link>
        ))}
      </section>
      <section className="journal-width engineering-section">
        <h2>{t('site.labels.approach')}</h2>
        <div>
          <p className="engineering-lead">{t('site.overview.body')}</p>
          <PassageList items={principles} />
          <Link to={href('/work-with-us')} className="journal-link">
            {t('journal.consultingLink')}
            <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </section>
      <section className="journal-width engineering-proof">
        <h2>{t('journal.productsTitle')}</h2>
        <div>
          <p>{t('site.productDepth.intro')}</p>
          <Link to={href('/products')} className="journal-link">
            {t('site.labels.products')}
            <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </section>
      <EngineeringCta />
    </div>
  )
}
type Industry = {
  title: string
  intro: string
  needs: Array<Passage>
  evidence: string
  link: string
}
export function IndustriesPage() {
  const { t, href } = usePage()
  const items = t('site.industries.items', {
    returnObjects: true,
  }) as unknown as Array<Industry>
  const paths = ['/iot', '/products', '/cloud-engineering']
  return (
    <div className="engineering-page">
      <PageIntro
        title={t('site.industries.title')}
        intro={t('site.industries.intro')}
      />
      <nav
        className="journal-width engineering-topic-nav"
        aria-label={t('journal.industriesTitle')}
      >
        {items.map((item, i) => (
          <a key={item.title} href={`#industry-${i}`}>
            {item.title}
            <ArrowRight size={16} aria-hidden="true" />
          </a>
        ))}
      </nav>
      {items.map((item, i) => (
        <section
          id={`industry-${i}`}
          key={item.title}
          className="engineering-industry"
        >
          <div className="journal-width">
            <div className="engineering-section">
              <div>
                <h2>{item.title}</h2>
                <p className="engineering-lead">{item.intro}</p>
              </div>
              <PassageList items={item.needs} />
            </div>
            <div className="engineering-industry-evidence">
              <p>{item.evidence}</p>
              <Link to={href(paths[i])} className="journal-link">
                {item.link}
                <ArrowUpRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      ))}
      <EngineeringCta />
    </div>
  )
}
export function WorkWithUsPage() {
  const { t, href, locale } = usePage()
  const steps = t('site.engagement.steps', {
    returnObjects: true,
  }) as unknown as Array<Passage & { output: string }>
  return (
    <div className="engineering-page">
      <PageIntro
        title={t('site.engagement.title')}
        intro={t('site.engagement.intro')}
      >
        <Link to={href('/contact')} className="journal-link">
          {t('site.engagement.contact')}
          <ArrowUpRight size={18} aria-hidden="true" />
        </Link>
      </PageIntro>
      <CollaborationPaths locale={locale} paths={steps} />
      <section className="journal-width engineering-proof">
        <h2>{t('site.engagement.startTitle')}</h2>
        <div>
          <p>{t('site.engagement.startBody')}</p>
          <Link to={href('/contact')} className="journal-link">
            {t('site.engagement.contact')}
            <ArrowUpRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </section>
      <section className="journal-width engineering-related">
        <h2>{t('site.labels.capabilities')}</h2>
        <div>
          {Object.entries(CAPABILITY_PATHS).map(([key, path]) => (
            <Link key={key} to={href(path)}>
              {t(`journal.capabilities.${key}.name`)}
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  )
}
